import { AxiosResponse } from 'axios';
import cookieSet from '../helpers/cookieSet';
import { TenantCodeType, UserType } from '../types/dataTypes';
import http from './http';
import { ErrorResponse } from '../types/configTypes';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_G_AUTH_ALIENT_ID;
const AZURE_CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID;
const AZURE_TENANT_ID = import.meta.env.VITE_AZURE_TENANT_ID;

type ErrorCallback = (error: string) => void;
type SuccessCallback = (data: UserType) => void;

const endpoint = '/v1/auth';

const createLoginSuccessHandler =
  (onSuccess: SuccessCallback) =>
  ({ data: user }: AxiosResponse<UserType, unknown>) =>
    onSuccess(user);

const createLoginErrorHandler =
  (onError: ErrorCallback) => (error: ErrorResponse) =>
    onError(error.response.data.error);

const googleAuthorize = (
  onSuccess: SuccessCallback,
  onError: ErrorCallback,
) => {
  const { google } = window;
  // remove auth cookie if it still exists
  cookieSet.auth.remove();

  const client = google.accounts.oauth2.initCodeClient({
    client_id: GOOGLE_CLIENT_ID as string,
    scope: 'email',
    ux_mode: 'popup',
    callback: response => {
      // Use the proper CodeResponse type from Google accounts
      const { code } = response;

      http
        .post(endpoint, { google_code: code })
        .then(createLoginSuccessHandler(onSuccess))
        .catch(createLoginErrorHandler(onError));
    },
  });

  client.requestCode();
};

const microsoftAuthorize = (
  onSuccess: SuccessCallback,
  onError: ErrorCallback,
) => {
  try {
    cookieSet.auth.remove();

    // Build the Microsoft authorization URL
    const authUrl = new URL(
      `https://login.microsoftonline.com/${AZURE_TENANT_ID}/oauth2/v2.0/authorize`,
    );
    const redirectUri = `${window.location.origin}/auth/microsoft_graph/callback`;

    authUrl.searchParams.append('client_id', AZURE_CLIENT_ID as string);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('redirect_uri', redirectUri);
    authUrl.searchParams.append('scope', 'User.Read email profile openid');
    authUrl.searchParams.append('response_mode', 'query');

    // Store the callback functions in sessionStorage to retrieve after redirect
    sessionStorage.setItem('msAuthSuccess', 'true');
    sessionStorage.setItem(
      'msAuthCallbacks',
      JSON.stringify({ onSuccess: onSuccess.name, onError: onError.name }),
    );

    // Redirect to Microsoft login
    window.location.href = authUrl.toString();
  } catch (error: unknown) {
    onError((error as Error).message || 'Microsoft authentication failed');
  }
};

// Function to handle the callback from Microsoft
export const handleMicrosoftCallback = (
  onSuccess: SuccessCallback,
  onError: ErrorCallback,
) => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const error = urlParams.get('error');

  if (error) {
    onError(`Microsoft authentication error: ${error}`);
    return;
  }

  if (code) {
    const redirectUri = `${window.location.origin}/auth/microsoft_graph/callback`;

    http
      .post(endpoint, {
        microsoft_code: code,
        redirect_uri: redirectUri,
      })
      .then(createLoginSuccessHandler(onSuccess))
      .catch(createLoginErrorHandler(onError));
  } else {
    onError('No authorization code received from Microsoft');
  }
};

export const manualAuthorize = (
  email: string,
  password: string,
  onSuccess: SuccessCallback,
  onError: ErrorCallback,
) => {
  // remove auth cookie if it still exists
  cookieSet.auth.remove();

  return http
    .post(endpoint, {
      username: email,
      password: password,
    })
    .then(createLoginSuccessHandler(onSuccess))
    .catch(createLoginErrorHandler(onError));
};

export const manualLogout = () => http.delete(endpoint);

export const validateTenant = (tenantCode: TenantCodeType) =>
  !!cookieSet.auth.get()?.tenants.find(tenant => tenant.code === tenantCode);

export { googleAuthorize, microsoftAuthorize };
export default googleAuthorize;
